import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { captureInviteToken } from './invite-token';
import App from './App';
import './styles.css';

/**
 * The first line of this file is the security-bearing one.
 *
 * `captureInviteToken()` runs **before** `posthog.init` (inside `App`'s analytics effect) and
 * **before** `ClerkProvider` mounts. It takes the invitation token out of `location.hash`
 * and out of the address bar, and holds it in `sessionStorage` instead (R9, U8):
 *
 * - PostHog reads `location.href` at init and on every replay snapshot. A fragment still in
 *   the bar at that moment is a live invitation in a vendor's store for seven days.
 * - Clerk's `SignIn routing="hash"` writes its own steps into the fragment. A token sitting
 *   there when the provider mounts is a token handed to whatever Clerk does with it.
 *
 * Nothing may be added above this call. An import with a side effect that touches the URL
 * would run first and the ordering would be silently gone, which is why the capture is a
 * statement here rather than something a component does on mount.
 *
 * The same call reads the Planner's hand-off on `/from-planner` (KTD4, KTD5): its fragment is
 * stripped, the bar is pointed at `/`, and `planner-handoff.ts` holds it in `sessionStorage`
 * across a sign-in. One call, so the ordering guarantee covers both fragments.
 *
 * The return value is deliberately dropped: `App` reads the held token itself, because the
 * invitee who had to sign in comes back through a fresh page load where the fragment is long
 * gone and the stored entry is all there is.
 *
 * The router goes **below** this line, and that ordering is load-bearing too. `BrowserRouter`
 * reads `window.location` when it mounts and `captureInviteToken` rewrites it with
 * `history.replaceState` — so the capture has to be finished before the router looks. It is a
 * statement here rather than something a component does on mount for exactly that reason.
 */
captureInviteToken();

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    {/*
      * Real URLs, served by the asset store's `not_found_handling: "single-page-application"`
      * (see wrangler.jsonc). A group room and a material page are addresses a teacher can
      * keep, send to a colleague in the same group, and reach on a cold load — and the back
      * button means what it says.
      */}
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
